import { useState, useEffect, useCallback } from 'react';
import { testimonials } from '../../../mocks/testimonials';

export default function ResidentTestimonials() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [direction, setDirection] = useState<'left' | 'right'>('right');
  const [isAnimating, setIsAnimating] = useState(false);

  const goTo = useCallback((index: number, dir: 'left' | 'right') => {
    if (isAnimating) return;
    setIsAnimating(true);
    setDirection(dir);
    setActiveIndex(index);
    setTimeout(() => setIsAnimating(false), 500);
  }, [isAnimating]);

  const goNext = useCallback(() => {
    const next = (activeIndex + 1) % testimonials.length;
    goTo(next, 'right');
  }, [activeIndex, goTo]);

  const goPrev = useCallback(() => {
    const prev = (activeIndex - 1 + testimonials.length) % testimonials.length;
    goTo(prev, 'left');
  }, [activeIndex, goTo]);

  useEffect(() => {
    if (!isAutoPlaying) return;
    const timer = setInterval(goNext, 6000);
    return () => clearInterval(timer);
  }, [isAutoPlaying, goNext]);

  const getVisibleIndices = () => {
    const prev = (activeIndex - 1 + testimonials.length) % testimonials.length;
    const next = (activeIndex + 1) % testimonials.length;
    return [prev, activeIndex, next];
  };

  const [prevIdx, , nextIdx] = getVisibleIndices();
  const current = testimonials[activeIndex];
  const prevTestimonial = testimonials[prevIdx];
  const nextTestimonial = testimonials[nextIdx];

  return (
    <section className="py-24 bg-gradient-to-b from-stone-50 to-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 rounded-full mb-5">
            <i className="ri-chat-quote-line text-teal-600"></i>
            <span className="text-teal-700 text-sm font-semibold tracking-wide uppercase">Local Voices</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Hear From Real Residents
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Discover why families, professionals, and retirees all call Roseville home
          </p>
        </div>

        {/* Carousel */}
        <div
          className="relative"
          onMouseEnter={() => setIsAutoPlaying(false)}
          onMouseLeave={() => setIsAutoPlaying(true)}
        >
          {/* Cards Container */}
          <div className="flex items-center justify-center gap-6 min-h-[420px]">
            {/* Previous Card (dimmed) */}
            <div
              className="hidden lg:block w-80 flex-shrink-0 opacity-40 scale-90 transition-all duration-500 cursor-pointer"
              onClick={goPrev}
            >
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 h-[380px] flex flex-col">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-teal-700 font-bold text-lg">{prevTestimonial.name.charAt(0)}</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-sm">{prevTestimonial.name}</h4>
                    <p className="text-gray-500 text-xs">{prevTestimonial.neighborhood}</p>
                  </div>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed line-clamp-6">
                  &ldquo;{prevTestimonial.quote}&rdquo;
                </p>
              </div>
            </div>

            {/* Active Card */}
            <div
              className={`w-full max-w-2xl flex-shrink-0 transition-all duration-500 ${
                isAnimating
                  ? direction === 'right'
                    ? 'animate-slideInRight'
                    : 'animate-slideInLeft'
                  : ''
              }`}
            >
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                <div className="p-10 md:p-12">
                  {/* Quote Icon */}
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-50 rounded-xl mb-8">
                    <i className="ri-double-quotes-l text-2xl text-teal-600"></i>
                  </div>

                  {/* Quote Text */}
                  <blockquote className="text-gray-800 text-lg md:text-xl leading-relaxed mb-8 font-medium">
                    &ldquo;{current.quote}&rdquo;
                  </blockquote>

                  {/* Divider */}
                  <div className="w-16 h-0.5 bg-teal-500 mb-8"></div>

                  {/* Author Info */}
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0 ring-2 ring-teal-100 ring-offset-2">
                        <span className="text-teal-700 font-bold text-xl">{current.name.charAt(0)}</span>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 text-lg">{current.name}</h4>
                        <p className="text-gray-500 text-sm">
                          {current.neighborhood} · {current.years} {current.years === 1 ? 'year' : 'years'} resident
                        </p>
                        <div className="flex items-center gap-0.5 mt-1">
                          {Array.from({ length: current.rating }).map((_, i) => (
                            <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>
                          ))}
                        </div>
                      </div>
                    </div>
                    <span className="px-4 py-1.5 bg-teal-50 text-teal-700 text-sm font-semibold rounded-full whitespace-nowrap">
                      {current.highlight}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Next Card (dimmed) */}
            <div
              className="hidden lg:block w-80 flex-shrink-0 opacity-40 scale-90 transition-all duration-500 cursor-pointer"
              onClick={goNext}
            >
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 h-[380px] flex flex-col">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-teal-700 font-bold text-lg">{nextTestimonial.name.charAt(0)}</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-sm">{nextTestimonial.name}</h4>
                    <p className="text-gray-500 text-xs">{nextTestimonial.neighborhood}</p>
                  </div>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed line-clamp-6">
                  &ldquo;{nextTestimonial.quote}&rdquo;
                </p>
              </div>
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={goPrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-lg border border-gray-200 hover:bg-teal-50 hover:border-teal-200 transition-all cursor-pointer z-10"
            aria-label="Previous testimonial"
          >
            <i className="ri-arrow-left-s-line text-xl text-gray-700"></i>
          </button>
          <button
            onClick={goNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-lg border border-gray-200 hover:bg-teal-50 hover:border-teal-200 transition-all cursor-pointer z-10"
            aria-label="Next testimonial"
          >
            <i className="ri-arrow-right-s-line text-xl text-gray-700"></i>
          </button>

          {/* Dots */}
          <div className="flex items-center justify-center gap-2 mt-10">
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                onClick={() => goTo(idx, idx > activeIndex ? 'right' : 'left')}
                className={`transition-all duration-300 rounded-full cursor-pointer ${
                  idx === activeIndex
                    ? 'w-8 h-2.5 bg-teal-500'
                    : 'w-2.5 h-2.5 bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Go to testimonial ${idx + 1}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes slideInRight {
          from { opacity: 0.5; transform: translateX(40px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes slideInLeft {
          from { opacity: 0.5; transform: translateX(-40px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .animate-slideInRight { animation: slideInRight 0.5s ease-out; }
        .animate-slideInLeft { animation: slideInLeft 0.5s ease-out; }
      `}</style>
    </section>
  );
}
